<template>
  <div class="user_page">
    <h3 class="title-page">{{ $t("ourRequest") }}</h3>
    <div>
      <div class="admin_container">
        <div class="division_main">
          <div style="gap: 15px" class="d-flex align-center mb-3">
            <v-btn
              class="mr-4"
              color="primary"
              dark
              min-height="38"
              @click="$refs.orderDialog.open()"
            >
              <vue-icon class="mr-2" icon="contractIcon"></vue-icon>
              {{ $t("create") }}
            </v-btn>
            <custom-input-text
              type="text"
              class="user_input no-margin"
              :placeholder="$t('productName')"
              @input="onChangeFilter"
              v-model="orderListFilter.productName"
            />
            <vue-select
              infinite
              clearable
              class="user_input"
              @item-resolved="setDepartment"
              @next-page-resolved="
                (data) => setDepartment([...departments, ...data])
              "
              :get-item-promise="
                ({ text, page }) =>
                  $store.dispatch('getDepartments', {
                    page,
                    name: text,
                    limit: 15,
                  })
              "
              :items="departments"
              v-model="orderListFilter.departmentId"
              @input="onChangeFilter"
              type="text"
            />
            <custom-select
              dense
              class="user_input no-margin mt-1"
              custom-selection
              custom-item
              :value="visibleHeaders"
              @input="setVisibleHeaders"
              multiple
              item-text="text"
              item-value="value"
              :items="headers"
            >
              <template #item="{ attrs, item }">
                <div
                  class="column-setting-title-wrap flex-grow-1"
                  @click="changeColumnSettings(item, attrs)"
                >
                  <base-checkbox
                    class="mr-2"
                    v-model="visibleHeaders"
                    :value="item.value"
                  />
                  <span class="v-list-item__title" style="width: 100%">{{
                    item.text
                  }}</span>
                </div>
              </template>
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0">{{ $t("columns") }}</span>
              </template>
            </custom-select>

            <custom-date-picker
              clearable
              hide-details
              :max="new Date() || orderListFilter.toDate"
              @input="onChangeFilter"
              v-model="orderListFilter.fromDate"
              name="calendar"
              color="#E3EBF3"
              class="data_item user_input data_item_calendar"
              icon="mdi-calendar-month small"
            ></custom-date-picker>
            <custom-date-picker
              :start-day="false"
              hide-details
              :min="orderListFilter.fromDate"
              :max="new Date()"
              clearable
              @input="onChangeFilter"
              v-model="orderListFilter.toDate"
              name="calendar_t"
              color="#E3EBF3"
              class="data_input_tw user_input data_item"
              icon="mdi-calendar-month small"
            ></custom-date-picker>
          </div>
          <DepartmentOrderTable
            :orders="orders"
            :filterableHeadersTable="filterableHeadersTable"
            :tableOptions="tableOptions"
            :getOrdersWithLoading="getOrdersWithLoading"
            :orderListFilter="orderListFilter"
            :productTypes="productTypes"
            :setProductType="setProductType"
            :setProductGroup="setProductGroup"
            :productGroups="productGroups"
            @openOrderDialog="$refs.orderDialog.open($event)"
            @openOrderHistoryDialog="$refs.orderHistoryDialog.open($event)"
            @addProduct="addProduct"
            @openOrderDialogView="$refs.orderDialogView.open($event)"
            @editDepartmentOrderForm="editDepartmentOrderForm"
            @deleteChildItem="deleteItem"
            @sendNotificationToOMTK="$refs.receptionDialog.open($event)"
            @openChangeRequestOrderItemDialog="
              $refs.changeRequestOrderItemDialog.open($event)
            "
            @editDivisionOrderItem="
              $refs.divisionOrderItemChangeStatusDialogRef.open($event)
            "
            @openOrderItemActFilesDialog="
              $refs.orderItemActFilesDialog.open($event)
            "
          ></DepartmentOrderTable>
          <VuePagination
            @update:limit="
              (limit) => {
                tableOptions.limit = limit;
                tableOptions.page = 1;
                changePage();
              }
            "
            v-model="tableOptions.page"
            :length="lastPage"
            @input="changePage"
            go-to
            :limit="tableOptions.limit"
          />
        </div>
      </div>
      <warning-dialog
        v-model="warningDialog"
        @apply="deleteOrder"
      ></warning-dialog>

      <reception-date-dialog ref="receptionDialog" />
    </div>
    <order-item-act-files-dialog ref="orderItemActFilesDialog" />
    <change-request-order-item-dialog
      ref="changeRequestOrderItemDialog"
      @saved="getOrdersWithLoading"
    />
    <division-order-dialog
      ref="departmentOrderForm"
      v-model="dialog"
      :update-table-order="getOrdersWithLoading"
    ></division-order-dialog>
    <order-dialog ref="orderDialog" @saved="onCreatedOrder"></order-dialog>
    <order-dialog-view ref="orderDialogView" />
    <order-history-dialog ref="orderHistoryDialog"></order-history-dialog>
    <DivisionOrderItemChangeStatusDialog
      @changed="getOrdersWithLoading"
      ref="divisionOrderItemChangeStatusDialogRef"
    />
    <DepartmentOrderNextInfoAfterCreateOrderTour
      ref="departmentOrderNextInfoAfterCreateOrderTourRef"
    />
  </div>
</template>

<script>
import WarningDialog from "@/components/ui/prompt/warning-dialog";
import { orderItemStatus, orderStatus, TABLES } from "@/utils/constants";
import {
  debounce,
  getQueryByKey,
  makerPagination,
  shallowEqual,
  trueSet,
} from "@/utils/pureFunctions";
import VueSelect from "@/components/ui/vue-select/VueSelect";
import SocketService from "@/services/SocketService";
import { departmentSocketService } from "@/utils/socket/department";
import ReceptionDateDialog from "@/components/division/ReceptionDateDialog";
import ChangeRequestOrderItemDialog from "@/components/division/ChangeRequestOrderItemDialog";
import OrderItemActFilesDialog from "@/components/order/OrderItemActFilesDialog";
import BaseCheckbox from "@/components/ui/BaseCheckbox";
import OrderDialog from "@/components/order-dialog/OrderDialog";
import gridTableMixin from "@/configuration/mixins/gridTableMixin";
import DivisionOrderDialog from "@/components/division-order-dialog/DivisionOrderDialog";
import OrderDialogView from "@/components/order-dialog-view/OrderDialogView";
import VuePagination from "@/components/ui/vue-pagination/VuePagination";
import OrderHistoryDialog from "@/components/order/OrderHistoryDialog";
import DivisionOrderItemChangeStatusDialog from "@/components/division/DivisionOrderItemChangeStatusDialog.vue";
import DepartmentOrderNextInfoAfterCreateOrderTour from "@/components/division/DepartmentOrderNextInfoAfterCreateOrderTour";
import DepartmentOrderTable from "@/components/division/DepartmentOrderTable.vue";
// import TableWithFilter from "../../../components/ui/table/TableWithFilter.vue";
// import SearchInput from "../../../plugins/Vuetify/components/SearchInput.vue";

const ORDER_LIST_FILTER = {
  orderNumber: null,
  contractNumber: null,
  positionState: null,
  productName: null,
  fromDate: null,
  toDate: null,
};

const ITEM_LIMIT = 200;
export default {
  components: {
    VueSelect,
    DepartmentOrderNextInfoAfterCreateOrderTour,
    DivisionOrderItemChangeStatusDialog,
    OrderHistoryDialog,
    VuePagination,
    OrderDialogView,
    DivisionOrderDialog,
    OrderDialog,
    BaseCheckbox,
    OrderItemActFilesDialog,
    ChangeRequestOrderItemDialog,
    ReceptionDateDialog,
    WarningDialog,
    DepartmentOrderTable,
  },
  name: "division-orders",
  data() {
    return {
      departments: [],
      justCreatedOrder: false,
      productTypes: [],
      productGroups: [],
      ready: false,
      editingOrder: {
        id: null,
        attachmentLink: null,
        attachmentFileName: null,
      },
      isShowFilter: false,
      warningDialog: false,
      tableOptions: {
        page: 1,
        sortBy: "state",
        sortDirection: false,
      },
      orderListFilter: {
        ...ORDER_LIST_FILTER,
      },
      lastPage: 0,
      dialog: false,
      orders: [],
      deleteOrderItem: {},
      ...orderStatus,
      ...orderItemStatus,
    };
  },
  computed: {
    orderItemStatus() {
      return Object.keys(orderItemStatus).map((item) => {
        return {
          name: this.$t(item),
          id: item,
        };
      });
    },
    filterableHeadersTable() {
      return this.headers.filter((item) => {
        return this.visibleHeaders.includes(item.value);
      });
    },
  },
  watch: {
    warningDialog(val) {
      if (!val) {
        this.deleteOrderItem = {};
      }
    },
  },
  methods: {
    setDepartment(departments) {
      this.departments = departments;
    },
    addProduct(item, isAdd) {
      this.$refs.orderDialog.open({ item }, isAdd);
    },
    editDepartmentOrderForm(item, child) {
      this.$refs.departmentOrderForm.editItem(item, child);
    },
    onCreatedOrder() {
      this.justCreatedOrder = true;
      this.getOrdersWithLoading();
    },
    setProductGroup(productGroups) {
      this.productGroups = productGroups;
    },
    setProductType(productTypes) {
      this.productTypes = productTypes;
    },
    setVisibleHeaders(headers) {
      this.visibleHeaders = headers;
    },
    async changePage(val) {
      console.log("changePage", this.tableOptions, this.orderListFilter, val);
      await this.$router.replace({
        query: { ...this.tableOptions, ...this.orderListFilter },
      });
      await this.getOrdersWithLoading();
    },

    onChangeFilter: debounce(function () {
      let query = { ...this.$route.query };
      if (query.sortDirection)
        query.sortDirection = trueSet(query.sortDirection);
      if (query.page) query.page = +query.page;
      const currentQuery = { ...this.tableOptions, ...this.orderListFilter };
      if (!shallowEqual(currentQuery, query)) {
        if (this.tableOptions.page !== 1) {
          this.tableOptions.page = 1;
        }
        this.$router
          .replace({ query: currentQuery })
          .catch((err) => console.warn(err));
        this.getOrdersWithLoading();
      }
    }, 300),
    hasRemainCount(allCount, givenCount) {
      if (allCount && givenCount) {
        return parseFloat(allCount) - parseFloat(givenCount) > 0;
      }
      return false;
    },
    async getOrders() {
      let params = {
        ...this.tableOptions,
        ...this.orderListFilter,
        limit: 10,
        page: this.tableOptions.page - 1,
      };
      try {
        const {
          data: { data, total: orderTotal },
        } = await this.$http.get("/office/orders/list-department", { params });
        this.orders = data.map((item, index) => {
          item.index = index + 1;
          item.loading = false;
          item.show = true;
          item.owner = trueSet(item.owner);
          item.itemsPageCount = 0;
          item.itemsCurrentPage = 1;
          if (item.itemCount) {
            item.itemsPageCount = makerPagination({
              limit: ITEM_LIMIT,
              total: +item.itemCount,
            }).lastPage;
          }
          return item;
        });

        this.lastPage = makerPagination({
          total: orderTotal,
          limit: params.limit,
        }).lastPage;
        if (this.justCreatedOrder) {
          this.$nextTick(() => {
            this.$refs.departmentOrderNextInfoAfterCreateOrderTourRef.start();
            this.justCreatedOrder = false;
          });
        }
      } catch (e) {
        this.setResponseError({ error: e });
      }
    },
    async getOrdersWithLoading() {
      this.showLoading();
      await this.getOrders();
      this.clearLoading();
    },
    deleteItem(item) {
      this.warningDialog = true;
      this.deleteOrderItem = item;
    },

    editItem(order, orderItem) {
      const { departmentOrderForm } = this.$refs;
      departmentOrderForm.editItem(order, orderItem);
      this.dialog = true;
    },
    async deleteOrder() {
      this.showLoading();
      try {
        const { id } = this.deleteOrderItem;
        await this.$http.delete("/office/orders/item-delete", {
          params: {
            order_item_id: id,
          },
        });
        await this.getOrders();
        this.warningDialog = false;
        this.setSuccess({
          title: this.$t("successfullyDeleted"),
        });
      } catch (e) {
        this.setResponseError({ error: e });
      } finally {
        this.clearLoading();
      }
    },
    async connectWebSocket() {
      try {
        const client = await SocketService.promiseConnect();
        client.subscribe("/user/order/reply", (data) => {
          data = JSON.parse(data.body);
          if (departmentSocketService[data.eventType]) {
            departmentSocketService[data.eventType].call(this, data);
          }
        });
      } catch (e) {
        console.log("ws error", e);
      }
    },
  },
  mixins: [gridTableMixin(TABLES.Order.slug)],
  async created() {
    let query = { ...this.$route.query };
    if (query.page && !isNaN(query.page)) query.page = +query.page;
    if (query.sortDirection) query.sortDirection = trueSet(query.sortDirection);
    this.tableOptions = getQueryByKey(this.tableOptions, query);
    this.orderListFilter = getQueryByKey(ORDER_LIST_FILTER, query);
    this.showLoading();
    try {
      await Promise.all([this.getColumns(), this.getOrders()]);
      this.ready = true;
    } catch (e) {
      this.setResponseError({ error: e });
    } finally {
      this.clearLoading();
    }
    // await this.connectWebSocket()
  },
  beforeDestroy() {
    // SocketService.disconnect()
  },
};
</script>
